import { useState } from "react";
import { Link, useLocation } from "wouter";
import { supabase } from "@/lib/supabase";

export default function AdminChangePassword() {
  const [, goTo] = useLocation();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (password.length < 12) {
      setError("Use at least 12 characters for your new password.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    setLoading(true);
    const {
      data: { user },
    } = await supabase.auth.getUser();
    const meta = (user?.user_metadata ?? {}) as Record<string, unknown>;
    const { error: updateErr } = await supabase.auth.updateUser({
      password,
      data: { ...meta, force_password_change: false },
    });
    setLoading(false);
    if (updateErr) {
      setError("Could not update your password. Please try again.");
      return;
    }
    goTo("/admin/overview");
  };

  return (
    <div className="min-h-screen bg-[#F5EBD8] flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md bg-white border border-slate-100 shadow-lg p-8" style={{ borderRadius: 2 }}>
        <p className="text-[10px] font-bold uppercase tracking-wider text-[#D4A853] mb-2">Admin security</p>
        <h1 className="text-2xl font-bold text-[#1B2A4A] mb-2">Set a new password</h1>
        <p className="text-sm text-slate-500 mb-6 leading-relaxed">
          Your account requires a new password before you can access the admin dashboard.
        </p>
        <form className="space-y-4" onSubmit={(e) => void handleSubmit(e)}>
          <div>
            <label htmlFor="admin-new-password" className="block text-xs font-bold uppercase tracking-wider text-[#1B2A4A] mb-1.5">
              New password
            </label>
            <input
              id="admin-new-password"
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full h-11 px-4 border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#D4A853]/40"
              style={{ borderRadius: 2 }}
              required
              minLength={12}
            />
          </div>
          <div>
            <label htmlFor="admin-confirm-password" className="block text-xs font-bold uppercase tracking-wider text-[#1B2A4A] mb-1.5">
              Confirm password
            </label>
            <input
              id="admin-confirm-password"
              type="password"
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="w-full h-11 px-4 border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#D4A853]/40"
              style={{ borderRadius: 2 }}
              required
              minLength={12}
            />
          </div>
          {error ? (
            <p className="text-sm text-rose-600" role="alert">
              {error}
            </p>
          ) : null}
          <button
            type="submit"
            disabled={loading}
            className="w-full h-11 font-semibold text-sm text-white bg-[#1B2A4A] hover:opacity-90 disabled:opacity-50 transition-opacity"
            style={{ borderRadius: 2 }}
          >
            {loading ? "Saving…" : "Update password"}
          </button>
        </form>
        <p className="text-xs text-slate-400 mt-6 text-center">
          <Link href="/" className="underline underline-offset-2 hover:text-slate-600">
            Back to site
          </Link>
        </p>
      </div>
    </div>
  );
}
